import { ResolvedSelectorConfig } from '../types';
import { isStableId } from '../patterns/autogenerated-ids';

/**
 * Shared helper that the `stableId` strategy (in this PR) and the
 * `fallback-css-path` walker (in the orchestration PR) both consult to decide
 * whether an element's id is usable as a selector anchor.
 *
 * Resolution order:
 *
 *   1. Check the customer's explicit-tracking attribute. If set with an empty
 *      value (e.g. `data-amp-track-id=""`), that's an explicit suppression
 *      signal — the customer is telling us to ignore this element's id even
 *      if it would otherwise look stable. Return null.
 *   2. Read the element's id. If absent or empty, return null.
 *   3. Check the id against the autogenerated-id pattern pack. If matched,
 *      return null (the algorithm should walk past this element).
 *   4. Otherwise return the id.
 *
 * The single point of consultation means the strategy and the fallback always
 * agree on what's a usable id — no chance of one component filtering an id
 * while another uses it anyway.
 */
export function getStableId(el: Element, cfg: ResolvedSelectorConfig): string | null {
  // 1. Suppression signal: empty explicit-tracking attribute on this element.
  const trackAttr = el.getAttribute(cfg.explicitTrackingAttribute);
  if (trackAttr !== null && trackAttr === '') {
    return null;
  }

  // 2. Id presence.
  const id = el.getAttribute('id');
  if (id === null || id === '') {
    return null;
  }

  // 3. Autogenerated-pattern filter.
  if (!isStableId(id, cfg.autogeneratedIdPatterns)) {
    return null;
  }

  // 4. Stable.
  return id;
}
